import React, { useEffect, useMemo, useState } from "react";
import Popup from "reactjs-popup";
import "reactjs-popup/dist/index.css";
import { QRCodeCanvas } from "qrcode.react";
import Styles from "./styles.module.scss";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
    cancelOrder,
    setPreSaleBuyModalStatus,
    setSettingsChangeWalletModalStatus
} from "../../../store/cabinet/action";

const formatCryptoAmount = (amount, decimals = 18) => {
    if (!amount) return "0";

    const value = String(amount);

    if (!decimals || decimals <= 0) {
        return value;
    }

    const paddedValue = value.padStart(decimals + 1, "0");

    const whole = paddedValue.slice(0, -decimals).replace(/^0+/, "") || "0";
    const fraction = paddedValue.slice(-decimals).replace(/0+$/, "");

    if (!fraction) {
        return whole;
    }

    return `${whole}.${fraction}`;
};

const truncateAddress = (address) => {
    if (!address) return "";
    return `${address.slice(0, 6)}...${address.slice(-6)}`;
};

const truncateReceiveAddress = (address) => {
    if (!address) return "";
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
};

const getCurrencyLabel = (option) => {
    const symbol = option?.currency?.symbol;
    const code = option?.currency?.code;

    if (!symbol && !code) return "";

    if (code?.includes("BEP20")) {
        return `${symbol} BEP20`;
    }

    return symbol || code;
};

const getChainLabel = (chain) => {
    if (!chain) return "";
    return chain.toUpperCase();
};

const getRemainingSeconds = (expiresAt) => {
    if (!expiresAt) return null;

    const expiresTime = new Date(expiresAt).getTime();

    if (Number.isNaN(expiresTime)) return null;

    return Math.max(0, Math.floor((expiresTime - Date.now()) / 1000));
};

const formatTimerValue = (seconds) => {
    if (seconds === null) return "";

    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    const paddedMinutes = String(minutes).padStart(2, "0");
    const paddedSeconds = String(secs).padStart(2, "0");

    if (hours > 0) {
        return `${String(hours).padStart(2, "0")}:${paddedMinutes}:${paddedSeconds}`;
    }

    return `${paddedMinutes}:${paddedSeconds}`;
};

const CabinetPreSaleBuyModal = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const preSaleBuyModalStatus = useSelector(store => store.cabinet.preSaleBuyModalStatus);
    const buyTokenData = useSelector(store => store.cabinet.buyTokenData);
    const userData = useSelector(store => store.cabinet.userData);
    const cancelOrderStatusStarted = useSelector(store => store.cabinet.cancelOrderStatusStarted);


    const receiveTokenAddress = userData?.user?.wallet_address || "";
    const hasReceiveTokenAddress = Boolean(receiveTokenAddress);

    const expiresAt = buyTokenData?.expires_at;

    const paymentOptions = useMemo(() => {
        return buyTokenData?.payment_options || [];
    }, [buyTokenData]);

    const primaryOptionId = useMemo(() => {
        return paymentOptions.find(option => option.is_primary)?.id || paymentOptions[0]?.id;
    }, [paymentOptions]);

    const [selectedOptionId, setSelectedOptionId] = useState(null);
    const [copied, setCopied] = useState(false);
    const [remainingSeconds, setRemainingSeconds] = useState(null);


    useEffect(() => {
        if (primaryOptionId) {
            setSelectedOptionId(primaryOptionId);
        }
    }, [primaryOptionId]);

    useEffect(() => {
        if (!preSaleBuyModalStatus || !expiresAt) {
            setRemainingSeconds(null);
            return;
        }

        const updateTimer = () => {
            setRemainingSeconds(getRemainingSeconds(expiresAt));
        };

        updateTimer();

        const timerId = setInterval(updateTimer, 1000);

        return () => {
            clearInterval(timerId);
        };
    }, [expiresAt, preSaleBuyModalStatus]);

    const selectedOption = useMemo(() => {
        return paymentOptions.find(option => option.id === selectedOptionId) || paymentOptions[0];
    }, [paymentOptions, selectedOptionId]);

    const selectedAddress = selectedOption?.wallet?.address || "";
    const selectedCurrency = selectedOption?.currency;
    const selectedSymbol = selectedCurrency?.symbol || "";
    const selectedChain = selectedCurrency?.chain || "";
    const selectedAmount = formatCryptoAmount(selectedOption?.amount, selectedCurrency?.decimals);

    const isOrderExpired = remainingSeconds !== null && remainingSeconds <= 0;

    const closeModal = () => {
        dispatch(setPreSaleBuyModalStatus(false));
    };

    const handleConnectWallet = () => {
        closeModal();
        dispatch(setSettingsChangeWalletModalStatus(true));
    };

    const copyAddress = async () => {
        if (!selectedAddress) return;

        try {
            await navigator.clipboard.writeText(selectedAddress);
            setCopied(true);

            setTimeout(() => {
                setCopied(false);
            }, 1200);
        } catch (error) {
            console.error("Copy failed:", error);
        }
    };

    const cancelSuccessHandler = () => {
        closeModal();
    }

    const cancelHandler = () =>{
        if (!cancelOrderStatusStarted){
            dispatch(cancelOrder(buyTokenData?.value, cancelSuccessHandler))
        }
    }

    return (
        <Popup
            open={preSaleBuyModalStatus}
            modal
            nested
            closeOnDocumentClick={false}
            onClose={closeModal}
            className="fixH"
        >
            <div className={Styles.modalContainer}>
                <div className={Styles.modalCloseBtn} onClick={closeModal}>
                    <div />
                    <div />
                </div>

                <div className={Styles.modalContentWrap}>
                    <div className={Styles.modalHeader}>
                        <div className={Styles.modalTitle}>
                            {t("Купить")}
                        </div>

                        {remainingSeconds !== null && (
                            <div className={`${Styles.orderTimer} ${isOrderExpired ? Styles.orderTimerExpired : ""}`}>
                                <span>
                                    {isOrderExpired ? t("Время истекло") : t("До окончания:")}
                                </span>

                                <strong>
                                    {formatTimerValue(remainingSeconds)}
                                </strong>
                            </div>
                        )}
                    </div>

                    <div className={Styles.modalContentBox}>
                        {paymentOptions.length > 0 && (
                            <>
                                <div className={Styles.currencyTabs}>
                                    {paymentOptions.map(option => (
                                        <button
                                            key={option.id}
                                            type="button"
                                            className={`${Styles.currencyTab} ${
                                                selectedOption?.id === option.id ? Styles.activeCurrencyTab : ""
                                            }`}
                                            onClick={() => setSelectedOptionId(option.id)}
                                        >
                                            {getCurrencyLabel(option)}
                                        </button>
                                    ))}
                                </div>

                                <div className={Styles.qrBox}>
                                    {selectedAddress && (
                                        <QRCodeCanvas
                                            value={selectedAddress}
                                            size={150}
                                            bgColor="transparent"
                                            fgColor="#ffffff"
                                            level="H"
                                            includeMargin={false}
                                        />
                                    )}
                                </div>

                                <div className={Styles.paymentInfo}>
                                    <div className={Styles.paymentInfoRow}>
                                        <span>{t("Сумма")}</span>
                                        <strong>
                                            {selectedAmount} {selectedSymbol}
                                        </strong>
                                    </div>

                                    <div className={Styles.paymentInfoRow}>
                                        <span>{t("Сеть")}</span>
                                        <strong>{getChainLabel(selectedChain)}</strong>
                                    </div>
                                </div>

                                <div className={Styles.addressLabel}>
                                    {t("Адрес кошелька")}
                                </div>

                                <div className={Styles.addressBox}>
                                    <div className={Styles.addressValue} title={selectedAddress}>
                                        {truncateAddress(selectedAddress)}
                                    </div>

                                    <button
                                        type="button"
                                        className={Styles.copyBtn}
                                        onClick={copyAddress}
                                    >
                                        {copied ? t("Скопировано") : t("Копировать")}
                                    </button>
                                </div>

                                <div className={Styles.warningRow}>
                                    <div className={Styles.warningText}>
                                        {t("Этот адрес принимает только")}{" "}
                                        <strong>{getCurrencyLabel(selectedOption)}</strong>{" "}
                                        {t("в сети")}{" "}
                                        <strong>{getChainLabel(selectedChain)}</strong>
                                        .{" "}
                                        {t("Оплачивайте только указанную сумму:")}{" "}
                                        <strong>
                                            {selectedAmount} {selectedSymbol}
                                        </strong>
                                        .{" "}
                                        {t("Отправляйте только выбранную валюту в указанной сети.")}
                                    </div>

                                    <button
                                        type="button"
                                        className={Styles.cancelBtn}
                                        onClick={()=>{cancelHandler()}}
                                    >
                                        {t("Отменить")}
                                    </button>
                                </div>

                                <div
                                    className={`${Styles.receiveTokenBox} ${
                                        !hasReceiveTokenAddress ? Styles.receiveTokenBoxError : ""
                                    }`}
                                >
                                    <div className={Styles.receiveTokenLabel}>
                                        {t("Ваш адрес для получения токенов")}
                                    </div>

                                    <div className={Styles.receiveTokenRow}>
                                        <div
                                            className={Styles.receiveTokenAddress}
                                            title={receiveTokenAddress}
                                        >
                                            {hasReceiveTokenAddress
                                                ? truncateReceiveAddress(receiveTokenAddress)
                                                : t("Кошелек не подключен")}
                                        </div>

                                        {!hasReceiveTokenAddress && (
                                            <button
                                                type="button"
                                                className={Styles.receiveTokenConnectBtn}
                                                onClick={handleConnectWallet}
                                            >
                                                {t("Подключить")}
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </>
                        )}

                        {paymentOptions.length === 0 && (
                            <div className={Styles.emptyText}>
                                {t("Payment data is not available")}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </Popup>
    );
};

export default CabinetPreSaleBuyModal;
